import {parse, stringify} from "../../../service/json/json-comment-wrapper";
import * as React from 'react';
import {useEffect, useRef, useState} from "react";
import {withNamespaces, WithNamespaces} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {FormField, Message} from "semantic-ui-react";
import CodeEditor from "../../../../core/components/CodeEditor/CodeEditor";
import {makeEditorStateByNameSelector} from "../../../../Layout/selectors/sidebar-editor";
import {ImmutableDDDWizardContext} from "../../../service/cody-wizard/context/ddd-action";
import {ApplyEventRule} from "../../../service/cody-wizard/event/get-event-metadata";
import {Schema} from "../../../service/cody-wizard/schema/schema";
import EditorToolbar from "./EditorToolbar";
import {makeEventApplyRulesSchema} from "./Validation/event-apply-rules-schema";
import {toggleSidebarEditor} from "../../../../Layout/actions/commands";

interface OwnProps {
  elementId: string;
  applyRules: ApplyEventRule[];
  eventSchema: Schema;
  informationSchema: Schema;
  ctx: ImmutableDDDWizardContext;
  onRulesChanged: (rules: ApplyEventRule[] | undefined) => void;
  autofocus?: boolean;
  readonly?: boolean;
  sessionId?: string;
}

type EventApplyRulesEditorProps = OwnProps & WithNamespaces;

const unsavedSessions: Record<string, string> = {};

const EventApplyRulesEditor = (props: EventApplyRulesEditorProps) => {
  const codeEditorRef = useRef<CodeEditor|null>(null);
  const [editorHasFocus, setEditorHasFocus] = useState(false);
  const [invalidJson, setInvalidJson] = useState(false);
  const editorState = useSelector(makeEditorStateByNameSelector('event-apply-rules'));
  const dispatch = useDispatch();

  let value = stringify(props.applyRules, null, 2);

  useEffect(() => {
    if (!codeEditorRef.current) {
      return;
    }


    let isInvalidJson = false;

    if(props.sessionId && typeof unsavedSessions[props.sessionId] === "string") {
      value = unsavedSessions[props.sessionId];
      isInvalidJson = true;
    }

    codeEditorRef.current!.initializeModel({
      fileId: `${props.elementId}-event-apply-rules.json`,
      language: "json",
      value,
      schema: makeEventApplyRulesSchema(props.eventSchema, props.informationSchema, props.ctx),
    });

    setInvalidJson(isInvalidJson);
  }, [props.elementId, value, props.eventSchema.toString(), props.informationSchema.toString()]);

  useEffect(() => {
    if(props.autofocus && codeEditorRef.current) {
      codeEditorRef.current.focus();
    }
  }, [props.autofocus])

  const propagateChanges = (editorStr: string, silentError?: boolean): boolean => {
    if(editorStr === '') {
      props.onRulesChanged(undefined);

      if(props.sessionId && unsavedSessions[props.sessionId]) {
        delete unsavedSessions[props.sessionId];
      }
      return true;
    }

    try {
      const changedRules = parse(editorStr);
      props.onRulesChanged(changedRules);

      if(props.sessionId && unsavedSessions[props.sessionId]) {
        delete unsavedSessions[props.sessionId];
      }

      return true;
    } catch (e) {
      console.error("[CodyWizard] Event Apply Rules Editor invalid JSON: ", e);
      if(!silentError) {
        setInvalidJson(true);
      }
      return false;
    }
  }

  return <FormField>
    {invalidJson && <Message error={true} size="small" content={props.t('insp.cody_wizard.schema_editor.invalid_json') as string} icon="warning" style={{display: 'flex'}}/>}

    <EditorToolbar expanded={editorState.expanded} double={editorState.double} onExpandChanged={(expand, double) => dispatch(toggleSidebarEditor('event-apply-rules', expand, !!double))}/>
    <CodeEditor
      containerId={`cody-event-apply-rules-editor-${props.sessionId}`}
      ref={codeEditorRef}
      repairJson={true}
      options={{
        fontSize: 12,
        folding: true,
        glyphMargin: false,
        lineNumbers: true,
        lineDecorationsWidth: 3,
        minimap: {
          enabled: false
        },
        formatOnPaste: true,
        scrollBeyondLastLine: false,
        mouseWheelZoom: true,
        automaticLayout: true,
        scrollbar: {
          alwaysConsumeMouseWheel: false
        },
        readOnly: props.readonly,
        fixedOverflowWidgets: true,
      }}
      className={"code editor" + (editorHasFocus ? ' focus' : '') + (editorState.expanded ? ' expanded' : '')  + (editorState.double ? ' double' : '')}
      onBlur={() => {
        if(codeEditorRef.current) {
          const changedRulesStr = codeEditorRef.current.retrievePayload();

          const validJson = propagateChanges(changedRulesStr);

          setEditorHasFocus(false);

          if(!validJson && props.sessionId) {
            unsavedSessions[props.sessionId] = changedRulesStr;
          }
        }
      }}
      onFocus={() => {
        setEditorHasFocus(true);
      }}
    />
  </FormField>
};

export default withNamespaces()(EventApplyRulesEditor);
